import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone, ChevronRight, ChevronDown } from "lucide-react";
import Wordmark from "@/components/Wordmark";
import { BUSINESS, SERVICES } from "@/data/content";

const WHO = [
  { label: "Our Story", to: "/our-story" },
  { label: "Our Team", to: "/team" },
  { label: "FAQ", to: "/faq" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [openMenu, setOpenMenu] = useState(false);
  const [hover, setHover] = useState(null); // 'who' | 'services'
  const [mobileSub, setMobileSub] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpenMenu(false);
    setMobileSub(null);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className="fixed left-0 right-0 z-40 transition-colors duration-300"
      style={{ top: "var(--announce-h, 34px)" }}
    >
      <div
        className={`w-full border-b transition-all duration-300 ${
          scrolled
            ? "bg-white/85 backdrop-blur-xl border-black/10"
            : "bg-white/60 backdrop-blur-md border-transparent"
        }`}
      >
        <div className="relative mx-auto flex max-w-[1400px] items-center justify-between px-5 md:px-10 h-[92px]">
          {/* Left nav (desktop) */}
          <nav className="hidden lg:flex items-center gap-8 text-[13px] uppercase tracking-[0.15em] text-[#050505]">
            <Link to="/" className="link-underline" data-testid="nav-home">
              Home
            </Link>

            {/* Who We Are */}
            <div
              className="relative"
              onMouseEnter={() => setHover("who")}
              onMouseLeave={() => setHover(null)}
            >
              <button className="link-underline inline-flex items-center gap-1 uppercase" data-testid="nav-who">
                Who We Are <ChevronDown size={13} className="mt-[1px]" />
              </button>
              <DropdownPanel open={hover === "who"}>
                {WHO.map((w) => (
                  <DropLink key={w.to} to={w.to}>
                    {w.label}
                  </DropLink>
                ))}
              </DropdownPanel>
            </div>

            {/* Services */}
            <div
              className="relative"
              onMouseEnter={() => setHover("services")}
              onMouseLeave={() => setHover(null)}
            >
              <Link
                to="/services"
                className="link-underline inline-flex items-center gap-1"
                data-testid="nav-services"
              >
                Services <ChevronDown size={13} className="mt-[1px]" />
              </Link>
              <DropdownPanel open={hover === "services"} wide>
                <div className="grid grid-cols-2 gap-x-10 gap-y-3">
                  {SERVICES.map((s) => (
                    <DropLink key={s.slug} to={`/services/${s.slug}`}>
                      {s.title}
                    </DropLink>
                  ))}
                </div>
              </DropdownPanel>
            </div>
          </nav>

          {/* Brand — left on mobile, centered on desktop */}
          <div className="lg:absolute lg:left-1/2 lg:top-1/2 lg:-translate-x-1/2 lg:-translate-y-1/2">
            <Wordmark compact />
          </div>

          {/* Right (desktop) */}
          <div className="hidden lg:flex items-center gap-6 justify-end text-[13px] uppercase tracking-[0.15em]">
            <Link to="/shop" className="link-underline" data-testid="nav-shop">
              Shop
            </Link>
            <Link to="/offers" className="link-underline" data-testid="nav-offers">
              Offers
            </Link>
            <Link to="/contact" className="link-underline" data-testid="nav-contact">
              Contact
            </Link>
            <a
              href={BUSINESS.bookingUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="nav-book"
              className="group ml-4 inline-flex items-center gap-2 bg-[#050505] px-6 py-3 text-white transition-colors duration-300 hover:bg-white hover:text-[#050505] border border-[#050505]"
            >
              Book Now
              <ChevronRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
          </div>

          {/* Mobile toggle */}
          <button
            className="lg:hidden ml-auto flex items-center justify-center w-11 h-11 -mr-1 text-[#050505]"
            onClick={() => setOpenMenu(true)}
            aria-label="Open menu"
            data-testid="mobile-menu-open"
          >
            <Menu size={32} strokeWidth={2.25} />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {openMenu && (
          <>
            <motion.div
              className="fixed inset-0 z-[45] bg-black/40 lg:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpenMenu(false)}
            />
            <motion.aside
              data-testid="mobile-menu-panel"
              className="fixed right-0 z-[55] w-[86%] max-w-sm bg-[#fdfdfd] lg:hidden overflow-y-auto"
              style={{
                top: "var(--announce-h, 34px)",
                height: "calc(100% - var(--announce-h, 34px))",
              }}
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="flex items-center justify-end px-6 h-[76px] border-b border-black/10">
                <button
                  onClick={() => setOpenMenu(false)}
                  aria-label="Close menu"
                  data-testid="mobile-menu-close"
                  className="w-10 h-10 flex items-center justify-center"
                >
                  <X size={24} />
                </button>
              </div>

              <nav className="flex flex-col px-6 py-4 text-[#050505]">
                <MobileLink to="/" label="Home" />
                <MobileAccordion
                  label="Who We Are"
                  open={mobileSub === "who"}
                  onToggle={() => setMobileSub(mobileSub === "who" ? null : "who")}
                  items={WHO}
                />
                <MobileAccordion
                  label="Services"
                  open={mobileSub === "services"}
                  onToggle={() =>
                    setMobileSub(mobileSub === "services" ? null : "services")
                  }
                  items={[
                    { label: "All Services", to: "/services" },
                    ...SERVICES.map((s) => ({ label: s.title, to: `/services/${s.slug}` })),
                  ]}
                />
                <MobileLink to="/shop" label="Shop" />
                <MobileLink to="/offers" label="Offers" />
                <MobileLink to="/contact" label="Contact" />
              </nav>

              <div className="px-6 pb-28 pt-2">
                <a
                  href={BUSINESS.phoneHref}
                  className="mb-3 flex items-center justify-center gap-2 border border-[#050505] py-4 text-sm uppercase tracking-[0.15em]"
                  data-testid="mobile-call"
                >
                  <Phone size={15} /> {BUSINESS.phoneDisplay}
                </a>
                <a
                  href={BUSINESS.bookingUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-center gap-2 bg-[#050505] py-4 text-sm uppercase tracking-[0.15em] text-white"
                  data-testid="mobile-book"
                >
                  Book Now <ChevronRight size={15} />
                </a>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </header>
  );
}

function DropdownPanel({ open, children, wide = false }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 10 }}
          transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
          className={`absolute left-0 top-full pt-5 ${wide ? "w-[540px]" : "w-56"}`}
        >
          <div className="bg-white border border-black/10 p-8 shadow-[0_20px_40px_-24px_rgba(0,0,0,0.35)]">
            {children}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function DropLink({ to, children }) {
  return (
    <Link
      to={to}
      className="block whitespace-nowrap py-2 text-[12px] uppercase tracking-[0.14em] text-[#333] transition-colors hover:text-black"
    >
      {children}
    </Link>
  );
}

function MobileLink({ to, label }) {
  return (
    <Link
      to={to}
      className="border-b border-black/10 py-4 font-serif-display text-2xl"
      data-testid={`mobile-link-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      {label}
    </Link>
  );
}

function MobileAccordion({ label, items, open, onToggle }) {
  return (
    <div className="border-b border-black/10">
      <button
        onClick={onToggle}
        className="flex w-full items-center justify-between py-4 font-serif-display text-2xl"
      >
        {label}
        <ChevronDown
          size={20}
          className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`}
        />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="flex flex-col pb-3 pl-3">
              {items.map((it) => (
                <Link
                  key={it.to}
                  to={it.to}
                  className="py-2 text-sm uppercase tracking-[0.12em] text-[#3d3d3d]"
                >
                  {it.label}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
